/**
 * Specialized React hooks for Files table operations
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createAirtableClient, buildFilter, filters } from './index';
import type { AirtableAttachment } from './types';
import type { FilterCondition, LogicalFilter } from './formula';

const BASE_ID = import.meta.env.VITE_AIRTABLE_BASE_ID;

// Types that match the Airtable Files table schema
export interface AirtableFile {
    id: string;
    name: string;
    uploadDate?: Date;
    source: 'Email' | 'Upload';
    status: 'Queued' | 'Processing' | 'Processed' | 'Attention';
    processingStatus?: 'UPL' | 'DETINV' | 'PARSE' | 'RELINV' | 'MATCHING' | 'MATCHED' | 'ERROR'; // New field
    pages?: number;
    isDuplicate: boolean;
    duplicateOf?: string[]; // Now a record link array
    relatedInvoices?: string[];
    activity?: string[];
    relatedEmails?: string[];
    attachments?: AirtableAttachment[]; // File attachments from Airtable
    fileHash?: string; // SHA-256 hash for duplicate detection
    errorCode?: string; // Error-Code from Airtable
    errorDescription?: string; // Error-Description from Airtable
    errorLink?: string; // Error-Link from Airtable
    // Computed fields
    isLinked: boolean; // Calculated field based on relationships
    createdAt?: Date;
    updatedAt?: Date;
    amount?: number;
}

export interface FileFilters {
    status?: string[];
    source?: string[];
    duplicates?: boolean;
    isLinked?: boolean;
}

export interface UseFilesOptions {
    autoFetch?: boolean;
    initialFilters?: FileFilters;
}

export interface UseFilesResult {
    files: AirtableFile[];
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    updateFile: (fileId: string, updates: Partial<AirtableFile>) => Promise<void>;
    createFile: (fileData: Partial<AirtableFile>) => Promise<AirtableFile>;
    deleteFile: (fileId: string) => Promise<void>;
    archiveFile: (fileId: string) => Promise<void>;
    linkToDocument: (fileId: string, documentId: string, documentType: string) => Promise<void>;
    unlinkFromDocument: (fileId: string, documentId: string) => Promise<void>;
    updateFilesInPlace: (updatedFiles: AirtableFile[]) => void;
}

export interface UseFileCountsResult {
    counts: {
        total: number;
        queued: number;
        processed: number;
        attention: number;
        bySource: {
            email: number;
            upload: number;
        };
        duplicates: number;
        linked: number;
    };
    loading: boolean;
}

/**
 * Transform Airtable record to AirtableFile
 */
function transformAirtableRecord(record: any): AirtableFile {
    const relatedInvoices = record.fields['InvoiceHeaderID'] || record.fields['Invoices'] || []; // New field name
    const relatedEmails = record.fields['Emails'] || []; 
    // Calculate isLinked based on non-email document links (invoices, POs, etc.)
    const isLinked = relatedInvoices.length > 0;
    
    // Check if this is a duplicate based on Error-Code field
    const errorCode = record.fields['Error-Code'] || '';
    const isDuplicate = errorCode === 'DUPLICATE_FILE';
    
    return {
        id: record.id,
        name: record.fields['FileName'] || record.fields['Name'] || '', // New field name is FileName
        uploadDate: record.fields['UploadedDate'] ? new Date(record.fields['UploadedDate']) : undefined,
        source: record.fields['Source'] || 'Upload',
        status: record.fields['Status'] || 'Queued',
        processingStatus: record.fields['Processing-Status'] || undefined, // New field
        pages: record.fields['Pages'] || undefined,
        isDuplicate: isDuplicate, // Now based on Error-Code field
        duplicateOf: [], // This field was removed from schema
        relatedInvoices,
        activity: record.fields['Activity'] || [],
        relatedEmails,
        attachments: record.fields['Attachments'] || [], // File attachments from Airtable
        fileHash: record.fields['FileHash'] || record.fields['File Hash'] || undefined, // New field name is FileHash
        errorCode: errorCode,
        errorDescription: record.fields['Error-Description'] || undefined,
        errorLink: record.fields['Error-Link'] || undefined,
        isLinked,
        createdAt: record.createdTime ? new Date(record.createdTime) : undefined,
        updatedAt: record.fields['Modified-At'] ? new Date(record.fields['Modified-At']) : undefined,
    };
}

/**
 * Transform AirtableFile to Airtable update format
 */
function transformToAirtableUpdate(file: Partial<AirtableFile>): any {
    const fields: any = {};
    
    if (file.name !== undefined) fields['FileName'] = file.name; // New field name
    if (file.uploadDate !== undefined) fields['UploadedDate'] = file.uploadDate?.toISOString().split('T')[0]; // Correct field name: UploadedDate
    // Source field removed in new schema
    // if (file.source !== undefined) fields['Source'] = file.source;
    if (file.status !== undefined) fields['Status'] = file.status;
    if (file.processingStatus !== undefined) fields['Processing-Status'] = file.processingStatus; // New field
    if (file.pages !== undefined) fields['Pages'] = file.pages;
    // Is Duplicate is controlled by Error-Code, so we don't set it directly
    // if (file.isDuplicate !== undefined) fields['Is Duplicate'] = file.isDuplicate;
    // Duplicate Of field was removed
    // if (file.duplicateOf !== undefined) fields['Duplicate Of'] = file.duplicateOf;
    
    return { fields };
}

/**
 * Build filter for files based on FileFilters
 */
function buildFileFilter(fileFilters: FileFilters): string {
    const filterParts: (FilterCondition | LogicalFilter)[] = [];
    
    if (fileFilters.status && fileFilters.status.length > 0) {
        if (fileFilters.status.length === 1) {
            filterParts.push(filters.equals('Status', fileFilters.status[0]));
        } else {
            const statusFilters = fileFilters.status.map(status => filters.equals('Status', status));
            filterParts.push(filters.or(...statusFilters));
        }
    }
    
    if (fileFilters.source && fileFilters.source.length > 0) {
        if (fileFilters.source.length === 1) {
            filterParts.push(filters.equals('Source', fileFilters.source[0]));
        } else {
            const sourceFilters = fileFilters.source.map(source => filters.equals('Source', source));
            filterParts.push(filters.or(...sourceFilters));
        }
    }
    
    if (fileFilters.duplicates === true) {
        filterParts.push(filters.equals('Is Duplicate', 'TRUE'));
    } else if (fileFilters.duplicates === false) {
        filterParts.push(filters.equals('Is Duplicate', 'FALSE'));
    }
    
    if (fileFilters.isLinked === true) {
        filterParts.push(filters.or(
            filters.not(filters.isEmpty('Related Invoices')),
            filters.not(filters.isEmpty('Related Emails'))
        ));
    } else if (fileFilters.isLinked === false) {
        filterParts.push(filters.and(
            filters.isEmpty('Related Invoices'),
            filters.isEmpty('Related Emails')
        ));
    }
    
    return filterParts.length > 0 ? buildFilter(filters.and(...filterParts)) : '';
}

/**
 * Main hook for Files operations
 */
export function useFiles(options: UseFilesOptions = {}): UseFilesResult {
    const { autoFetch = false, initialFilters } = options;
    const [files, setFiles] = useState<AirtableFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // We don't need the client here since we're using API routes directly

    const fetchFiles = useCallback(async (fileFilters?: FileFilters) => {
        setLoading(true);
        setError(null);
        
        try {
            const client = createAirtableClient(BASE_ID);
            
            const listParams: any = {
                sort: [{ field: 'FileName', direction: 'asc' as const }],
                pageSize: 100
            };

            const filterFormula = fileFilters ? buildFileFilter(fileFilters) : '';
            if (filterFormula) {
                listParams.filterByFormula = filterFormula;
            }

            const data = await client.listRecords('Files', listParams);
            const transformedFiles = data.records.map(transformAirtableRecord);
            setFiles(transformedFiles);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to fetch files';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, []);

    const refresh = useCallback(() => fetchFiles(initialFilters), [fetchFiles, initialFilters]);

    const updateFile = useCallback(async (fileId: string, updates: Partial<AirtableFile>) => {
        try {
            const client = createAirtableClient(BASE_ID);
            const updateData = transformToAirtableUpdate(updates);
            
            await client.updateRecords('Files', {
                records: [{ id: fileId, ...updateData }]
            });
            
            // Update local state optimistically
            setFiles(currentFiles => 
                currentFiles.map(file => 
                    file.id === fileId ? { ...file, ...updates } : file
                )
            );
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update file';
            throw new Error(message);
        }
    }, []);

    const createFile = useCallback(async (fileData: Partial<AirtableFile>): Promise<AirtableFile> => {
        try {
            const client = createAirtableClient(BASE_ID);
            const createData = transformToAirtableUpdate(fileData);
            
            const data = await client.createRecords('Files', {
                records: [createData]
            });
            
            const newFile = transformAirtableRecord(data.records[0]);
            
            // Update local state
            setFiles(currentFiles => [...currentFiles, newFile]);
            
            return newFile;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to create file';
            throw new Error(message);
        }
    }, []);

    const deleteFile = useCallback(async (fileId: string) => {
        try {
            const client = createAirtableClient(BASE_ID);
            await client.deleteRecords('Files', { records: [fileId] });
            
            // Update local state
            setFiles(currentFiles => currentFiles.filter(file => file.id !== fileId));
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete file';
            throw new Error(message);
        }
    }, []);

    const archiveFile = useCallback(async (fileId: string) => {
        await updateFile(fileId, { status: 'Processed' }); // Archive means processed
    }, [updateFile]);

    const linkToDocument = useCallback(async (fileId: string, documentId: string, documentType: string) => {
        const file = files.find(f => f.id === fileId);
        if (!file) throw new Error('File not found');

        // Add to appropriate relation field based on document type
        if (documentType === 'invoices') {
            const currentInvoices = file.relatedInvoices || [];
            if (!currentInvoices.includes(documentId)) {
                await updateFile(fileId, {
                    relatedInvoices: [...currentInvoices, documentId],
                    status: 'Processed'
                });
            }
        }
        // Add more document types as needed
    }, [files, updateFile]);

    const unlinkFromDocument = useCallback(async (fileId: string, documentId: string) => {
        const file = files.find(f => f.id === fileId);
        if (!file) throw new Error('File not found');

        const updatedInvoices = (file.relatedInvoices || []).filter(id => id !== documentId);
        const isStillLinked = updatedInvoices.length > 0 || (file.relatedEmails && file.relatedEmails.length > 0);
        
        await updateFile(fileId, {
            relatedInvoices: updatedInvoices,
            status: isStillLinked ? 'Processed' : 'Queued'
        });
    }, [files, updateFile]);

    /**
     * Update files in place without re-fetching (for real-time updates)
     */
    const updateFilesInPlace = useCallback((updatedFiles: AirtableFile[]) => {
        setFiles(prevFiles => {
            const updatedMap = new Map(updatedFiles.map(file => [file.id, file]));
            
            // Update existing files or add new ones
            const updated = prevFiles.map(file => 
                updatedMap.has(file.id) ? updatedMap.get(file.id)! : file
            );
            
            // Add any new files that weren't in the list
            const existingIds = new Set(prevFiles.map(f => f.id));
            const newFiles = updatedFiles.filter(f => !existingIds.has(f.id));
            
            return [...newFiles, ...updated];
        });
    }, []);

    // Auto-fetch on mount if enabled
    useEffect(() => {
        if (autoFetch) {
            fetchFiles(initialFilters);
        }
    }, [autoFetch, fetchFiles, initialFilters]);

    return {
        files,
        loading,
        error,
        refresh,
        updateFile,
        createFile,
        deleteFile,
        archiveFile,
        linkToDocument,
        unlinkFromDocument,
        updateFilesInPlace,
    };
}

/**
 * Hook for file counts and statistics
 */
export function useFileCounts(): UseFileCountsResult {
    const [counts, setCounts] = useState({
        total: 0,
        queued: 0,
        processed: 0,
        attention: 0,
        bySource: {
            email: 0,
            upload: 0,
        },
        duplicates: 0,
        linked: 0,
    });
    const [loading, setLoading] = useState(true);

    const fetchCounts = useCallback(async () => {
        try {
            // Check if BASE_ID is available
            if (!BASE_ID) {
                console.warn('AIRTABLE_BASE_ID not configured, skipping file counts fetch');
                setLoading(false);
                return;
            }

            const client = createAirtableClient(BASE_ID);
            const data = await client.listRecords('Files', { pageSize: 100 });
            const files = data.records.map(transformAirtableRecord);
            
            const newCounts = {
                total: files.length,
                queued: files.filter(f => f.status === 'Queued').length,
                processed: files.filter(f => f.status === 'Processed').length,
                attention: files.filter(f => f.status === 'Attention').length,
                bySource: {
                    email: files.filter(f => f.source === 'Email').length,
                    upload: files.filter(f => f.source === 'Upload').length,
                },
                duplicates: files.filter(f => f.isDuplicate).length,
                linked: files.filter(f => (f.relatedInvoices?.length || 0) > 0).length,
            };
            
            setCounts(newCounts);
        } catch (err) {
            console.error('Failed to fetch file counts:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCounts();
    }, []);

    return { counts, loading };
}
